export type EcuadorMapGroup = "Nacional" | "Sierra" | "Costa" | "Amazonía" | "Insular" | "Provincia";

export type EcuadorMapPack = {
  id: string;
  file: string;
  terrainFile: string | null;
  name: string;
  shortName: string;
  group: EcuadorMapGroup;
  provinces: string[];
  aliases: string[];
  detail: string;
  bounds: [number, number, number, number];
  center: [number, number];
  zoom: number;
  maxZoom: number;
  priority: number;
  legacy?: boolean;
  overview?: boolean;
};

/**
 * Catálogo único de cartografía THERA.
 *
 * - Azuay y Cañar mantienen exactamente los nombres de archivo ya publicados.
 * - Los paquetes regionales usan el mismo vector Protomaps v4, estilo THERA y
 *   relieve Terrarium corregido para agua (v3).
 * - Los límites incluyen un pequeño solape operativo para evitar franjas vacías
 *   al acercarse a una frontera provincial. No sustituyen límites oficiales.
 */
export const ECUADOR_MAP_PACKS: EcuadorMapPack[] = [
  {
    id: "azuay",
    file: "azuay-detallado.pmtiles",
    terrainFile: "azuay-relieve-v3.pmtiles",
    name: "Azuay detallado",
    shortName: "Azuay",
    group: "Provincia",
    provinces: ["Azuay"],
    aliases: ["azuay", "cuenca", "cajas", "sayausi", "sayousi", "gualaceo", "chordeleg", "sigsig", "girón", "giron"],
    detail: "Cuenca, Sayausí, Cajas, vías, senderos, agua, nombres y relieve 3D corregido",
    bounds: [-79.8, -3.68, -78.38, -2.5],
    center: [-79.08, -3.05],
    zoom: 10,
    maxZoom: 15,
    priority: 100,
    legacy: true,
  },
  {
    id: "canar",
    file: "canar-detallado.pmtiles",
    terrainFile: "canar-relieve-v3.pmtiles",
    name: "Cañar detallado",
    shortName: "Cañar",
    group: "Provincia",
    provinces: ["Cañar"],
    aliases: ["cañar", "canar", "azogues", "cojitambo", "biblián", "biblian", "la troncal"],
    detail: "Azogues, Cojitambo, caminos, agua, etiquetas y relieve 3D corregido",
    bounds: [-79.58, -2.88, -78.52, -2.17],
    center: [-79.02, -2.57],
    zoom: 10,
    maxZoom: 15,
    priority: 100,
    legacy: true,
  },
  {
    id: "ecuador",
    file: "ecuador-esencial.pmtiles",
    terrainFile: "ecuador-esencial-relieve-v3.pmtiles",
    name: "Ecuador esencial",
    shortName: "Ecuador",
    group: "Nacional",
    provinces: [],
    aliases: ["ecuador"],
    detail: "Mapa nacional completo con caminos, senderos, agua, nombres y relieve 3D corregido",
    bounds: [-92.25, -5.15, -75.0, 1.8],
    center: [-78.35, -1.45],
    zoom: 5,
    maxZoom: 15,
    priority: 1,
    overview: true,
  },
  {
    id: "sierra-sur",
    file: "sierra-sur-detallado.pmtiles",
    terrainFile: "sierra-sur-relieve-v3.pmtiles",
    name: "Sierra Sur",
    shortName: "Sierra Sur",
    group: "Sierra",
    provinces: ["Loja", "Azuay", "Cañar"],
    aliases: ["loja", "saraguro", "catamayo", "vilcabamba", "sierra sur"],
    detail: "Loja, Azuay y Cañar · paquete continuo con solape fronterizo",
    bounds: [-80.35, -5.15, -77.95, -2.05],
    center: [-79.2, -3.62],
    zoom: 8,
    maxZoom: 15,
    priority: 40,
  },
  {
    id: "sierra-centro-oeste",
    file: "sierra-centro-oeste-detallado.pmtiles",
    terrainFile: "sierra-centro-oeste-relieve-v3.pmtiles",
    name: "Sierra Centro Oeste",
    shortName: "Centro Oeste",
    group: "Sierra",
    provinces: ["Bolívar", "Chimborazo"],
    aliases: ["bolívar", "bolivar", "guaranda", "chimborazo", "riobamba", "colta", "sierra centro oeste"],
    detail: "Bolívar y Chimborazo · volcanes, páramo, vías y senderos",
    bounds: [-79.65, -2.75, -77.85, -1.05],
    center: [-78.72, -1.88],
    zoom: 8.5,
    maxZoom: 15,
    priority: 40,
  },
  {
    id: "sierra-centro-este",
    file: "sierra-centro-este-detallado.pmtiles",
    terrainFile: "sierra-centro-este-relieve-v3.pmtiles",
    name: "Sierra Centro Este",
    shortName: "Centro Este",
    group: "Sierra",
    provinces: ["Cotopaxi", "Tungurahua"],
    aliases: ["cotopaxi", "latacunga", "tungurahua", "ambato", "baños", "banos", "sierra centro este"],
    detail: "Cotopaxi y Tungurahua · corredor volcánico y acceso oriental",
    bounds: [-79.35, -2.05, -77.3, -0.45],
    center: [-78.42, -1.25],
    zoom: 8.5,
    maxZoom: 15,
    priority: 40,
  },
  {
    id: "sierra-norte",
    file: "sierra-norte-detallado.pmtiles",
    terrainFile: "sierra-norte-relieve-v3.pmtiles",
    name: "Sierra Norte",
    shortName: "Sierra Norte",
    group: "Sierra",
    provinces: ["Pichincha", "Imbabura", "Carchi"],
    aliases: ["pichincha", "quito", "imbabura", "ibarra", "otavalo", "carchi", "tulcán", "tulcan", "sierra norte"],
    detail: "Pichincha, Imbabura y Carchi · volcanes, lagunas y frontera norte",
    bounds: [-79.45, -0.85, -77.25, 1.55],
    center: [-78.3, 0.3],
    zoom: 8,
    maxZoom: 15,
    priority: 40,
  },
  {
    id: "costa-sur",
    file: "costa-sur-detallado.pmtiles",
    terrainFile: "costa-sur-relieve-v3.pmtiles",
    name: "Costa Sur",
    shortName: "Costa Sur",
    group: "Costa",
    provinces: ["El Oro", "Guayas", "Santa Elena"],
    aliases: ["el oro", "machala", "guayas", "guayaquil", "santa elena", "salinas", "costa sur"],
    detail: "El Oro, Guayas y Santa Elena · litoral, manglar y cordillera costera",
    bounds: [-81.25, -4.55, -79.05, -1.7],
    center: [-80.25, -2.85],
    zoom: 8,
    maxZoom: 15,
    priority: 40,
  },
  {
    id: "costa-centro",
    file: "costa-centro-detallado.pmtiles",
    terrainFile: "costa-centro-relieve-v3.pmtiles",
    name: "Costa Centro",
    shortName: "Costa Centro",
    group: "Costa",
    provinces: ["Los Ríos", "Manabí"],
    aliases: ["los ríos", "los rios", "babahoyo", "quevedo", "manabí", "manabi", "manta", "portoviejo", "costa centro"],
    detail: "Los Ríos y Manabí · costa, bosque seco, ciudades y vías rurales",
    bounds: [-81.25, -2.35, -78.9, 0.25],
    center: [-80.12, -1.05],
    zoom: 8,
    maxZoom: 15,
    priority: 40,
  },
  {
    id: "costa-norte",
    file: "costa-norte-detallado.pmtiles",
    terrainFile: "costa-norte-relieve-v3.pmtiles",
    name: "Costa Norte",
    shortName: "Costa Norte",
    group: "Costa",
    provinces: ["Esmeraldas", "Santo Domingo de los Tsáchilas"],
    aliases: ["esmeraldas", "atacames", "muisne", "santo domingo", "tsáchilas", "tsachilas", "costa norte"],
    detail: "Esmeraldas y Santo Domingo de los Tsáchilas · bosque húmedo y litoral",
    bounds: [-80.35, -0.4, -78.25, 1.55],
    center: [-79.35, 0.58],
    zoom: 8,
    maxZoom: 15,
    priority: 40,
  },
  {
    id: "amazonia-sur",
    file: "amazonia-sur-detallado.pmtiles",
    terrainFile: "amazonia-sur-relieve-v3.pmtiles",
    name: "Amazonía Sur",
    shortName: "Amazonía Sur",
    group: "Amazonía",
    provinces: ["Zamora Chinchipe", "Morona Santiago"],
    aliases: ["zamora chinchipe", "zamora", "yacuri", "morona santiago", "macas", "amazonía sur", "amazonia sur", "oriente sur"],
    detail: "Zamora Chinchipe y Morona Santiago · selva, cordillera y ríos",
    bounds: [-79.25, -5.15, -76.15, -1.45],
    center: [-77.68, -3.25],
    zoom: 7.5,
    maxZoom: 15,
    priority: 40,
  },
  {
    id: "amazonia-centro",
    file: "amazonia-centro-detallado.pmtiles",
    terrainFile: "amazonia-centro-relieve-v3.pmtiles",
    name: "Amazonía Centro",
    shortName: "Amazonía Centro",
    group: "Amazonía",
    provinces: ["Pastaza", "Napo"],
    aliases: ["pastaza", "puyo", "napo", "tena", "amazonía centro", "amazonia centro", "oriente centro"],
    detail: "Pastaza y Napo · piedemonte, selva, ríos y áreas protegidas",
    bounds: [-78.75, -2.45, -75.35, -0.15],
    center: [-77.05, -1.25],
    zoom: 7.5,
    maxZoom: 15,
    priority: 40,
  },
  {
    id: "amazonia-norte",
    file: "amazonia-norte-detallado.pmtiles",
    terrainFile: "amazonia-norte-relieve-v3.pmtiles",
    name: "Amazonía Norte",
    shortName: "Amazonía Norte",
    group: "Amazonía",
    provinces: ["Orellana", "Sucumbíos"],
    aliases: ["orellana", "coca", "sucumbíos", "sucumbios", "lago agrio", "cuyabeno", "yasuní", "yasuni", "amazonía norte", "amazonia norte", "oriente norte"],
    detail: "Orellana y Sucumbíos · Yasuní, Cuyabeno, ríos y accesos",
    bounds: [-78.25, -0.95, -74.95, 1.55],
    center: [-76.55, 0.28],
    zoom: 7.5,
    maxZoom: 15,
    priority: 40,
  },
  {
    id: "galapagos",
    file: "galapagos-detallado.pmtiles",
    terrainFile: "galapagos-relieve-v3.pmtiles",
    name: "Región Insular · Galápagos",
    shortName: "Galápagos",
    group: "Insular",
    provinces: ["Galápagos"],
    aliases: ["galápagos", "galapagos", "san cristóbal", "san cristobal", "santa cruz", "isabela"],
    detail: "Archipiélago de Galápagos · islas, poblados, senderos y relieve",
    bounds: [-92.25, -1.85, -89.05, 1.8],
    center: [-90.7, -0.62],
    zoom: 7,
    maxZoom: 15,
    priority: 40,
  },
];

function normalise(value: string) {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("es");
}

export function mapPackById(id?: string | null) {
  return ECUADOR_MAP_PACKS.find((pack) => pack.id === id) ?? null;
}

export function mapPackForText(value: string) {
  const target = normalise(value);
  return [...ECUADOR_MAP_PACKS]
    .filter((pack) => !pack.overview)
    .sort((a, b) => b.priority - a.priority)
    .find((pack) => [...pack.aliases, ...pack.provinces, pack.name, pack.shortName]
      .some((alias) => target.includes(normalise(alias)))) ?? null;
}

export function pointInsideBounds(longitude: number, latitude: number, bounds: EcuadorMapPack["bounds"]) {
  return longitude >= bounds[0] && longitude <= bounds[2]
    && latitude >= bounds[1] && latitude <= bounds[3];
}

export function mapPackForPoint(longitude: number, latitude: number, preferredText = "") {
  const textMatch = preferredText ? mapPackForText(preferredText) : null;
  if (textMatch && pointInsideBounds(longitude, latitude, textMatch.bounds)) return textMatch;
  return [...ECUADOR_MAP_PACKS]
    .filter((pack) => !pack.overview && pointInsideBounds(longitude, latitude, pack.bounds))
    .sort((a, b) => {
      if (b.priority !== a.priority) return b.priority - a.priority;
      const areaA = (a.bounds[2] - a.bounds[0]) * (a.bounds[3] - a.bounds[1]);
      const areaB = (b.bounds[2] - b.bounds[0]) * (b.bounds[3] - b.bounds[1]);
      return areaA - areaB;
    })[0] ?? mapPackById("ecuador");
}

function contourFileForPack(file: string) {
  if (!file.endsWith("-detallado.pmtiles")) return null;
  return file.replace(/-detallado\.pmtiles$/, "-curvas-v1.pmtiles");
}

export const MAP_OBJECT_NAMES = ECUADOR_MAP_PACKS.flatMap((pack) =>
  [pack.file, pack.terrainFile, contourFileForPack(pack.file)]
    .filter((file): file is string => Boolean(file)),
);

export const PUBLIC_VIEWER_MAPS = new Set(MAP_OBJECT_NAMES);
